"use client"

import { useState, type ReactNode } from "react"

import { ArtifactCanvasHeader } from "@/components/artifact/artifact-canvas-header"
import { ArtifactCanvasHeaderSlotProvider } from "@/components/artifact/artifact-canvas-header-slot"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
} from "@/components/ui/sheet"
import { artifactCanvasSectionPaddingClass } from "@/lib/artifact-canvas-section-padding"
import { cn } from "@/lib/utils"

type EditableArtifactCanvasShellProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  portalContainer: HTMLElement | null
  title?: ReactNode
  children?: ReactNode
  preventAutoFocus?: boolean
}

export function EditableArtifactCanvasShell({
  open,
  onOpenChange,
  portalContainer,
  title = "Editable artifact canvas",
  children,
  preventAutoFocus = false,
}: EditableArtifactCanvasShellProps) {
  const [headerTrailing, setHeaderTrailing] = useState<ReactNode>(null)
  const [titleOverride, setTitleOverride] = useState<ReactNode | null>(null)
  const [titleAddon, setTitleAddon] = useState<ReactNode>(null)

  return (
    <Sheet modal={false} open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        variant="pane"
        portalContainer={portalContainer}
        hideCloseButton
        className="gap-0 p-0"
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={preventAutoFocus ? (event) => event.preventDefault() : undefined}
      >
        <div className="flex h-full min-h-0 flex-col">
          <ArtifactCanvasHeader
            title={titleOverride ?? title}
            titleAddon={titleAddon}
            trailing={headerTrailing}
          />
          <ArtifactCanvasHeaderSlotProvider
            onHeaderActionsChange={setHeaderTrailing}
            onTitleChange={setTitleOverride}
            onTitleAddonChange={setTitleAddon}
          >
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {children ? (
                children
              ) : (
                <div className={cn('space-y-4', artifactCanvasSectionPaddingClass)}>
                  <p className="text-sm text-muted-foreground">
                    No editable artifact content is currently available for this canvas.
                  </p>
                  <SheetFooter className={cn('rounded-md bg-background/95', artifactCanvasSectionPaddingClass)}>
                    <div className="flex justify-end">
                      <SheetClose asChild>
                        <Button type="button" variant="secondary">
                          Close
                        </Button>
                      </SheetClose>
                    </div>
                  </SheetFooter>
                </div>
              )}
            </div>
          </ArtifactCanvasHeaderSlotProvider>
        </div>
      </SheetContent>
    </Sheet>
  )
}
